import { faEdit, faFile } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Toast } from '@modusign/design-system';
import { useRemoveAndCreateExternalDocumentRoles } from '@modusign/openapi-client-external-document';
import { TemplateRoleResponseName } from '@modusign/openapi-client-template/model';
import { useCallback, useState } from 'react';

import {
  ResourceModal,
  ResourceModalBody,
  ResourceModalHeaderWithBackButton,
} from '@app/components/Modal/ResourceModal';
import { ResourceRoleSubject } from '@app/features/authorization';
import {
  BatchPermissionGrantInlineAlert,
  BatchPermissionGrantModalFooter,
  BatchPermissionGrantModalRoleSubjectList,
  BatchPermissionGrantModalSubHeader,
} from '@app/features/authorization/batchPermissionGrant';
import ResourceRoleTypeSelect from '@app/features/authorization/components/ResourceRoleTypeSelect';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';

function formatTitle(titles: string[]) {
  if (titles.length <= 1) {
    return titles[0] || '';
  }

  return `'${titles[0]}' 외 ${titles.length - 1}개의 외부문서`;
}

interface ExternalDocumentBatchPermissionGrantModalProps {
  documents: Array<{ id: string; title: string }>;
  isOpen: boolean;
  onClose: () => void;
}

function ExternalDocumentBatchPermissionGrantModal({
  documents,
  isOpen,
  onClose,
}: ExternalDocumentBatchPermissionGrantModalProps) {
  const workspaceId = useWorkspaceId();

  const [templateRoleName, setTemplateRoleName] = useState<TemplateRoleResponseName>('VIEWER');
  const [selectedResourceRoleSubjects, setSelectedResourceRoleSubjects] = useState<
    ResourceRoleSubject[]
  >([]);

  const { createErrorHandler } = useOpenErrorModal({ keepOnUnmount: true });
  const { mutateAsync, isLoading } = useRemoveAndCreateExternalDocumentRoles({
    mutation: {
      onSuccess: ({ results, errors }) => {
        Toast.success({
          description: `${results.length - errors}개의 외부문서의 공유 설정을 적용했습니다. `,
        });
      },
      onError: createErrorHandler({ title: '공유 설정 적용 실패' }),
      onSettled: onClose,
    },
  });

  const handleGrant = useCallback(
    async (selectedResourceRoleSubjects: ResourceRoleSubject[]) => {
      await mutateAsync({
        workspaceId,
        data: {
          externalDocumentIds: documents.map((d) => d.id),
          roles: selectedResourceRoleSubjects.flatMap((subject) => ({
            subjectType: subject.subjectType,
            subjectId: subject.subjectId,
            name: templateRoleName,
          })),
        },
      });
    },
    [mutateAsync, templateRoleName, documents, workspaceId]
  );

  return (
    <ResourceModal isOpen={isOpen} onRequestClose={onClose}>
      <ResourceModalHeaderWithBackButton
        resourceName={formatTitle(documents.map((d) => d.title))}
        title="공유"
        onClose={onClose}
      />
      <ResourceModalBody>
        <BatchPermissionGrantModalSubHeader count={selectedResourceRoleSubjects.length}>
          <ResourceRoleTypeSelect
            roleOptions={[
              {
                roleName: 'VIEWER',
                name: '열람 가능',
                content: '열람 가능',
                icon: <FontAwesomeIcon icon={faFile} />,
              },
              {
                roleName: 'EDITOR',
                name: '편집자',
                content: '이용·수정·삭제 가능',
                icon: <FontAwesomeIcon icon={faEdit} />,
              },
            ]}
            selectedRoleName={templateRoleName}
            setSelectedRoleName={setTemplateRoleName}
          />
        </BatchPermissionGrantModalSubHeader>
        <BatchPermissionGrantInlineAlert />
        <BatchPermissionGrantModalRoleSubjectList
          resourceRoleSubjects={selectedResourceRoleSubjects}
          setResourceRoleSubjects={setSelectedResourceRoleSubjects}
        />
      </ResourceModalBody>
      <BatchPermissionGrantModalFooter
        disabled={selectedResourceRoleSubjects.length === 0}
        isLoading={isLoading}
        resourceName="외부문서"
        shouldAlertGrantAllMember={selectedResourceRoleSubjects.some(
          (rr) => rr.subjectType === 'GROUP' && rr.groupType === 'ALL_MEMBERS'
        )}
        targetCount={documents.length}
        onCancel={onClose}
        onGrant={() => handleGrant(selectedResourceRoleSubjects)}
        onResetGrant={() => handleGrant([])}
      />
    </ResourceModal>
  );
}

export default ExternalDocumentBatchPermissionGrantModal;
